import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Divider from "@material-ui/core/Divider";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import PageCardHeader from "components/Card/PageCardHeader.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import Icon from "@material-ui/core/Icon";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "220px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #0d1b6e 0%, #1a237e 60%, #1565c0 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    color: "#fff"
  },
  heroTitle: { fontSize: "32px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },

  // KPI stat cards
  kpiRow: { display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: "200px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s ease",
    border: "1px solid #f1f5f9",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 30px 0 rgba(0,0,0,0.08)" }
  },
  iconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "20px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "12px", color: "#64748b", margin: "2px 0 0", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" },

  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  semesterName: { fontWeight: "700", color: "#1a237e" },
  statusBadge: {
    padding: "4px 10px", borderRadius: "12px", fontSize: "10px", fontWeight: "800",
    textTransform: "uppercase", display: "inline-block"
  }
};

const BLANK = { name: "Semester 1", academicYearId: "", startDate: "", endDate: "", isCurrent: false };
const BLANK_EVENT = { type: "other", title: "", date: "" };
const EVENT_TYPES = [
  { value: "enrollment_open", label: "Enrollment Opens" },
  { value: "enrollment_close", label: "Enrollment Closes" },
  { value: "fee_deadline", label: "Fee Deadline" },
  { value: "exam_start", label: "Exams Start" },
  { value: "exam_end", label: "Exams End" },
  { value: "results_release", label: "Results Release" },
  { value: "other", label: "Other" }
];

class SemesterList extends Component {
  state = {
    tab: 0,
    semesters: [], academicYears: [],
    loading: true, saving: false,
    open: false, openYear: false,
    editing: null,
    form: { ...BLANK },
    yearForm: { name: "", startDate: "", endDate: "" },
    error: null,
    eventsOpen: false, eventsSemester: null, events: [], eventsLoading: false,
    eventForm: { ...BLANK_EVENT }, eventSaving: false, eventError: null
  };

  componentDidMount() { this.fetchData(); }

  fetchData = async () => {
    try {
      const [sRes, yRes] = await Promise.all([
        api.get("/semesters?limit=100"),
        api.get("/academic-years?limit=100").catch(() => ({ data: { data: [] } }))
      ]);
      const sd = sRes.data.data || sRes.data;
      const yd = yRes.data.data || yRes.data;
      this.setState({
        semesters: Array.isArray(sd) ? sd : sd.data || [],
        academicYears: Array.isArray(yd) ? yd : yd.data || [],
        loading: false
      });
    } catch (e) { this.setState({ loading: false }); }
  };

  openNew = () => this.setState({ open: true, editing: null, form: { ...BLANK }, error: null });
  handleClose = () => this.setState({ open: false, openYear: false });
  handleChange = field => e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, [field]: v } })); };
  handleYearChange = field => e => { const v = e.target.value; this.setState(prev => ({ yearForm: { ...prev.yearForm, [field]: v } })); };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.name || !form.academicYearId) { this.setState({ error: "Name and academic year are required." }); return; }
    this.setState({ saving: true });
    try {
      if (editing) await api.put(`/semesters/${editing}`, form);
      else await api.post("/semesters", form);
      this.setState({ open: false, saving: false });
      this.fetchData();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  handleSetCurrent = async (id) => {
    try {
      await api.patch(`/semesters/${id}/set-current`);
      this.fetchData();
    } catch (e) { /* silent */ }
  };

  handleSaveYear = async () => {
    const { yearForm } = this.state;
    if (!yearForm.name) { this.setState({ error: "Academic year name is required." }); return; }
    this.setState({ saving: true });
    try {
      await api.post("/academic-years", yearForm);
      this.setState({ openYear: false, saving: false });
      this.fetchData();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed.", saving: false });
    }
  };

  openEvents = async (semester) => {
    this.setState({ eventsOpen: true, eventsSemester: semester, eventForm: { ...BLANK_EVENT }, eventError: null });
    this.fetchEvents(semester.id);
  };

  fetchEvents = async (semesterId) => {
    this.setState({ eventsLoading: true });
    try {
      const res = await api.get(`/semesters/${semesterId}/events`);
      const d = res.data.data || res.data;
      this.setState({ events: Array.isArray(d) ? d : d.data || [], eventsLoading: false });
    } catch (e) {
      this.setState({ events: [], eventsLoading: false });
    }
  };

  handleEventChange = field => e => { const v = e.target.value; this.setState(prev => ({ eventForm: { ...prev.eventForm, [field]: v } })); };

  handleAddEvent = async () => {
    const { eventsSemester, eventForm } = this.state;
    if (!eventForm.title.trim() || !eventForm.date) {
      this.setState({ eventError: "Title and date are required." });
      return;
    }
    this.setState({ eventSaving: true, eventError: null });
    try {
      await api.post(`/semesters/${eventsSemester.id}/events`, eventForm);
      this.setState({ eventForm: { ...BLANK_EVENT }, eventSaving: false });
      this.fetchEvents(eventsSemester.id);
    } catch (e) {
      this.setState({ eventError: e.response?.data?.message || "Failed to add date.", eventSaving: false });
    }
  };

  handleDeleteEvent = async (id) => {
    const { eventsSemester } = this.state;
    try {
      await api.delete(`/semester-events/${id}`);
      this.fetchEvents(eventsSemester.id);
    } catch (e) { /* silent */ }
  };

  render() {
    const { classes } = this.props;
    const {
      semesters, academicYears, loading, open, openYear, form, yearForm, saving, error, tab = 0,
      eventsOpen, eventsSemester, events, eventsLoading, eventForm, eventSaving, eventError
    } = this.state;

    const semesterColumns = [
      {
        id: "name",
        header: "Semester",
        accessorFn: s => s.name,
        cell: info => <span className={classes.semesterName}>{info.getValue()}</span>
      },
      { id: "academicYear", header: "Academic Year", accessorFn: s => s.academicYear?.name || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => (
          <Pill tone={info.row.original.isCurrent ? "success" : "muted"}>
            {info.row.original.isCurrent ? "Current" : "Inactive"}
          </Pill>
        )
      },
      {
        id: "duration",
        header: "Duration",
        accessorFn: s =>
          `${s.startDate ? new Date(s.startDate).toLocaleDateString() : "—"} to ${s.endDate ? new Date(s.endDate).toLocaleDateString() : "—"}`
      },
      {
        id: "actions",
        header: "",
        cell: info => (
          <div style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
            <Button size="sm" color="info" simple onClick={() => this.openEvents(info.row.original)} style={{ padding: "6px" }}>
              <Icon style={{ fontSize: "20px" }}>event_note</Icon>
            </Button>
            {!info.row.original.isCurrent && (
              <Button size="sm" color="success" simple onClick={() => this.handleSetCurrent(info.row.original.id)} style={{ padding: "6px" }}>
                <Icon style={{ fontSize: "20px" }}>check_circle</Icon>
              </Button>
            )}
          </div>
        )
      }
    ];

    const yearColumns = [
      {
        id: "name",
        header: "Year Name",
        accessorFn: y => y.name,
        cell: info => <span className={classes.semesterName}>{info.getValue()}</span>
      },
      {
        id: "current",
        header: "Current",
        cell: info => (
          <Pill tone={info.row.original.isCurrent ? "success" : "muted"}>
            {info.row.original.isCurrent ? "Current" : "Inactive"}
          </Pill>
        )
      },
      { id: "actions", header: "", cell: () => "" }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <div className={classes.heroWrap}>
            <div className={classes.heroOverlay} />
            <div className={classes.heroContent}>
              <h1 className={classes.heroTitle}>Academic Timeline</h1>
              <p className={classes.heroSub}>
                Manage semesters, academic years, and the institution's active calendar.
              </p>
              <div style={{ marginTop: "20px", display: "flex", gap: "12px" }}>
                <Button color="white" onClick={this.openNew}>
                  <Icon style={{ marginRight: "8px" }}>add</Icon> New Semester
                </Button>
                <Button color="white" simple onClick={() => this.setState({ openYear: true, error: null })} style={{ border: "1px solid rgba(255,255,255,0.4)" }}>
                  <Icon style={{ marginRight: "8px" }}>calendar_today</Icon> Add Year
                </Button>
              </div>
            </div>
          </div>
        </GridItem>

        <GridItem xs={12}>
          <div className={classes.kpiRow}>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e3f2fd", color: "#1565c0" }}>
                <Icon>event</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{semesters.length}</p>
                <p className={classes.statLabel}>Total Semesters</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e8f5e9", color: "#2e7d32" }}>
                <Icon>check_circle</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{semesters.find(s => s.isCurrent)?.name || "None"}</p>
                <p className={classes.statLabel}>Active Semester</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e8eaf6", color: "#3949ab" }}>
                <Icon>calendar_today</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{academicYears.length}</p>
                <p className={classes.statLabel}>Academic Years</p>
              </div>
            </div>
          </div>
        </GridItem>

        <GridItem xs={12}>
          <ViewToolbar
            tabs={["Semesters", "Academic Years"]}
            activeTab={tab}
            onTabChange={t => this.setState({ tab: t })}
            actions={[]}
          />
          {tab === 0 ? (
            <DataTable
              columns={semesterColumns}
              data={semesters}
              isLoading={loading}
              emptyState="No semesters found."
            />
          ) : (
            <DataTable
              columns={yearColumns}
              data={academicYears}
              isLoading={loading}
              emptyState="No academic years found."
            />
          )}
        </GridItem>

        {/* New Semester Dialog */}
        <Dialog open={open} onClose={this.handleClose} maxWidth="sm" fullWidth>
          <DialogHeader title="New Semester" />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <FormControl className={classes.formControl}>
              <InputLabel>Semester Name</InputLabel>
              <Select value={form.name} onChange={this.handleChange("name")}>
                {["Semester 1", "Semester 2"].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Academic Year *</InputLabel>
              <Select value={form.academicYearId} onChange={this.handleChange("academicYearId")}>
                <MenuItem value="">— Select —</MenuItem>
                {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>)}
              </Select>
            </FormControl>
            <CustomDatePicker labelText="Start Date" id="startDate" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.startDate, onChange: this.handleChange("startDate") }} />
            <CustomDatePicker labelText="End Date" id="endDate" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.endDate, onChange: this.handleChange("endDate") }} />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={this.handleClose} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>

        {/* New Academic Year Dialog */}
        <Dialog open={openYear} onClose={this.handleClose} maxWidth="sm" fullWidth>
          <DialogHeader title="New Academic Year" />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput labelText="Name (e.g. 2024/2025) *" id="yearname" formControlProps={{ fullWidth: true }}
              inputProps={{ value: yearForm.name, onChange: this.handleYearChange("name") }} />
            <CustomDatePicker labelText="Start Date" id="ystartDate" formControlProps={{ fullWidth: true }}
              inputProps={{ value: yearForm.startDate, onChange: this.handleYearChange("startDate") }} />
            <CustomDatePicker labelText="End Date" id="yendDate" formControlProps={{ fullWidth: true }}
              inputProps={{ value: yearForm.endDate, onChange: this.handleYearChange("endDate") }} />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={this.handleClose} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSaveYear} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>

        {/* Key Dates Dialog */}
        <Dialog open={eventsOpen} onClose={() => this.setState({ eventsOpen: false })} maxWidth="sm" fullWidth>
          <DialogHeader
            title="Key Dates"
            subtitle={eventsSemester ? `Academic calendar for ${eventsSemester.name}` : ""}
            icon="event_note"
            color="info"
            onClose={() => this.setState({ eventsOpen: false })}
          />
          <DialogContent>
            {eventError && <p className={classes.errorMsg}>{eventError}</p>}

            {eventsLoading ? (
              <div style={{ textAlign: "center", padding: "20px" }}><CircularProgress size={24} /></div>
            ) : events.length === 0 ? (
              <p style={{ color: "#64748b", fontSize: "14px" }}>No key dates added yet for this semester.</p>
            ) : (
              <div style={{ marginBottom: "20px" }}>
                {events.map(ev => (
                  <div key={ev.id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 0", borderBottom: "1px solid #f1f5f9"
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>{ev.title}</div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>
                        {EVENT_TYPES.find(t => t.value === ev.type)?.label || ev.type} · {ev.date ? new Date(ev.date).toLocaleDateString() : "—"}
                      </div>
                    </div>
                    <Button size="sm" color="danger" simple onClick={() => this.handleDeleteEvent(ev.id)} style={{ padding: "6px" }}>
                      <Icon style={{ fontSize: "18px" }}>delete</Icon>
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <Divider className={classes.formControl} />

            <FormControl className={classes.formControl}>
              <InputLabel>Type</InputLabel>
              <Select value={eventForm.type} onChange={this.handleEventChange("type")}>
                {EVENT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            <CustomInput labelText="Title *" id="eventTitle" formControlProps={{ fullWidth: true }}
              inputProps={{ value: eventForm.title, onChange: this.handleEventChange("title"), placeholder: "e.g. Semester 1 enrollment closes" }} />
            <CustomDatePicker labelText="Date *" id="eventDate" formControlProps={{ fullWidth: true }}
              inputProps={{ value: eventForm.date, onChange: this.handleEventChange("date") }} />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ eventsOpen: false })} disabled={eventSaving}>Close</Button>
            <Button color="primary" onClick={this.handleAddEvent} disabled={eventSaving}>
              {eventSaving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Add Date"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

SemesterList.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(SemesterList));
